import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  HiOutlineCalendar,
  HiOutlineLink,
  HiOutlineDuplicate,
  HiOutlineCheck,
  HiOutlineQuestionMarkCircle,
  HiOutlineExternalLink,
} from 'react-icons/hi';

/**
 * Google Calendar section (Settings → Calendar in both panels).
 *
 * 1. CONNECT — the user pastes their Google Calendar ID; it is embedded
 *    below so the actual Google Calendar shows right in this section.
 * 2. SYNC — the CRM publishes a private iCal feed of follow-ups and
 *    scheduled tasks; the user adds it once in Google Calendar
 *    ("Other calendars → From URL") and Google pulls CRM activities in
 *    automatically from then on.
 *
 * Props:
 *   scope         — unique query key suffix ('org' | 'superadmin')
 *   fetchSettings — () => promise resolving to { googleCalendarId, feedUrl }
 *   saveSettings  — (googleCalendarId) => promise
 */
const GoogleCalendarSection = ({ scope, fetchSettings, saveSettings }) => {
  const queryClient = useQueryClient();
  const [calendarId, setCalendarId] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['calendar-settings', scope],
    queryFn: fetchSettings,
  });

  const settings = data || {};

  useEffect(() => {
    if (typeof settings.googleCalendarId === 'string') {
      setCalendarId(settings.googleCalendarId);
    }
  }, [settings.googleCalendarId]);

  const saveMutation = useMutation({
    mutationFn: saveSettings,
    onSuccess: () => {
      queryClient.invalidateQueries(['calendar-settings', scope]);
      toast.success('Google Calendar connected');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to connect calendar');
    },
  });

  const handleCopyFeed = async () => {
    try {
      await navigator.clipboard.writeText(settings.feedUrl || '');
      setCopied(true);
      toast.success('Feed URL copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the URL and copy it manually');
    }
  };

  const connected = Boolean(calendarId.trim());

  return (
    <div className="space-y-4">
      {/* ── Step 1: connect + embed ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineCalendar className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900">Google Calendar</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Paste your Google Calendar ID below and the calendar shows right here in this
              section.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
            placeholder="you@gmail.com  or  abc123@group.calendar.google.com"
            className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={() => saveMutation.mutate(calendarId.trim())}
            disabled={saveMutation.isLoading || !calendarId.trim()}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {saveMutation.isLoading ? 'Saving…' : connected ? 'Update' : 'Connect'}
          </button>
        </div>

        {/* How to find the Calendar ID */}
        <button
          onClick={() => setShowHelp((v) => !v)}
          className="mt-3 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          <HiOutlineQuestionMarkCircle className="w-4 h-4" />
          How do I find my Google Calendar ID?
          {showHelp ? ' ▲' : ' ▼'}
        </button>
        {showHelp && (
          <ol className="mt-2 ml-5 list-decimal space-y-1 text-xs text-gray-600">
            <li>Open calendar.google.com on a computer and sign in.</li>
            <li>Click the ⚙️ Settings menu → <strong>Settings</strong>.</li>
            <li>
              In the left panel choose the calendar under <strong>Settings for my calendars</strong>.
            </li>
            <li>
              Scroll to <strong>Integrate calendar</strong> — the <strong>Calendar ID</strong> is
              shown there (it looks like an email address).
            </li>
            <li>
              Copy it and paste it above. For the calendar to display here, its events must be
              visible: in that calendar's settings set <strong>Make available to public</strong> (or
              share it with "See all event details").
            </li>
          </ol>
        )}

        {/* Embedded Google Calendar */}
        {isLoading ? (
          <div className="mt-4 h-[420px] rounded-lg bg-gray-100 animate-pulse" />
        ) : connected ? (
          <div className="mt-4 rounded-lg overflow-hidden border border-gray-200">
            <iframe
              key={calendarId}
              title="Google Calendar"
              src={`https://calendar.google.com/calendar/embed?src=${encodeURIComponent(
                calendarId.trim()
              )}&ctz=Asia/Kolkata&showPrint=0&showNav=1&showTabs=1&showCalendars=1&mode=MONTH`}
              style={{ border: 0, width: '100%', minHeight: 480 }}
              className="block"
            />
            {/* Fallback: a private calendar (or a signed-out browser) renders a blank embed,
                and cross-origin iframes can't report that failure — so always offer a way out. */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Calendar looks blank? It is probably private — set <strong>Make available to public</strong> in
                its Google Calendar settings, or sign in to Google in this browser.
              </p>
              <a
                href={`https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(calendarId.trim())}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap"
              >
                <HiOutlineExternalLink className="w-4 h-4" />
                Open in Google Calendar
              </a>
            </div>
          </div>
        ) : (
          <div className="mt-4 h-40 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-center px-6">
            <HiOutlineCalendar className="w-8 h-8 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">
              Your Google Calendar will appear here once you connect its ID above.
            </p>
          </div>
        )}
      </div>

      {/* ── Step 2: sync CRM activities into Google ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <HiOutlineLink className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900">
              Sync CRM follow-ups & tasks into Google
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Add this private feed once in Google Calendar and every follow-up and scheduled task
              from the CRM shows on your Google Calendar — it stays updated automatically.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="mt-4 h-16 rounded-lg bg-gray-100 animate-pulse" />
        ) : (
          <>
            <div className="mt-4 flex items-stretch gap-2 flex-col sm:flex-row">
              <div className="flex-1 px-3.5 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700 font-mono break-all">
                {settings.feedUrl || '—'}
              </div>
              <button
                onClick={handleCopyFeed}
                disabled={!settings.feedUrl}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {copied ? (
                  <HiOutlineCheck className="w-4 h-4" />
                ) : (
                  <HiOutlineDuplicate className="w-4 h-4" />
                )}
                {copied ? 'Copied' : 'Copy URL'}
              </button>
            </div>

            <div className="mt-3 text-xs text-gray-600 leading-relaxed">
              <p className="font-semibold text-gray-700 mb-1">One-time setup in Google Calendar:</p>
              <ol className="ml-4 list-decimal space-y-1">
                <li>Copy the feed URL above.</li>
                <li>In Google Calendar, click <strong>Other calendars</strong> → <strong>+</strong> → <strong>From URL</strong>.</li>
                <li>Paste the URL and click <strong>Add calendar</strong>.</li>
                <li>
                  Done — the CRM calendar appears in your list. Google refreshes subscribed feeds
                  about once a day (or via the ⟳ refresh icon next to the calendar).
                </li>
              </ol>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleCalendarSection;